import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '@/api/settings.api';
import { authApi, type LookPrefs } from '@/api/auth.api';
import { ScreenHint } from './ScreenHint';
import { TwoFactorNudge } from '@/features/auth/TwoFactorNudge';
import { applyAppearance } from '@/lib/appearance';
import {
  applyCalLift,
  applyCalmMode,
  applyCalNewHighlight,
  applyCalPulse,
  applyCalPulseBurst,
  applyCalPulseCycle,
  applyCalStyle,
} from '@/lib/cal-style';
import { applyChartStyle } from '@/lib/chart-style';
import { IdleLock } from './IdleLock';
import { OfflineBanner } from './OfflineBanner';
import { InstallPrompt } from './InstallPrompt';
import { StatusMetaProvider } from '@/app/status-meta';
import { CalendarPrefsProvider } from '@/app/calendar-prefs';
import { NotificationBell } from '@/components/NotificationBell';
import { MoreMenu } from '@/components/ui/MoreMenu';
import { DEMO_VIEWS } from './demo-view';
import { OPEN_PANOU } from './landing';
import { CommandCentre } from './CommandCentre';
import { DuePanel } from './DuePanel';
import { Activity, BookOpen, Sparkles } from 'lucide-react';
import { AssistantPanel } from './AssistantPanel';
import { menuStyleKey, navIcon } from '@/lib/nav-icons';
import { useSession } from './session';
import { reportsApi } from '@/api/reports.api';
import { branchLimitOf } from '@/lib/branch-scope';
import { paymentsApi } from '@/api/payments.api';
import { appointmentsApi } from '@/api/appointments.api';
import { chatApi } from '@/api/chat.api';
import { inquiriesApi } from '@/api/inquiries.api';
import { filesApi } from '@/api/files.api';
import { addDays, toIsoDate } from '@/lib/calendar';
import { formatMoney, formatNumber } from '@/lib/format';
import { pageTitleFor } from './pageTitle';
import './AppLayout.css';
import { useTranslation } from 'react-i18next';

export interface NavItem {
  to: string;
  label: string;
  /**
   * The icon is not here: it comes from the clinic's chosen set in
   * `nav-icons.tsx`, keyed by `to`. See there for why an entry's identity is
   * its path, not its drawing.
   */
  /**
   * Hidden when the user does not hold this — or, for a list, any of it.
   * Absent means always shown.
   */
  permission?: string | string[];
  /**
   * Hidden when the clinic does not have this module — out of its contract, or
   * switched off on Setări → Personalizare → Module. The key is the module's
   * in `feature-catalogue.ts`; `npm run check:modules` holds the two together.
   */
  feature?: string;
  /**
   * The whole clinic's figures: hidden from a member limited to some
   * branches, whom the API refuses them (docs/FILIALE-ACCES.md).
   */
  clinicWide?: boolean;
  /** Only match the exact path, for the index route. */
  end?: boolean;
  /** Key into the live badge counts, when the entry carries one. */
  badge?: 'payments' | 'stock' | 'waitlist' | 'chat' | 'hr' | 'lab' | 'inquiries';
}

/**
 * The menu, in the reference system's own order.
 *
 * Not alphabetical, not grouped by module: it is the order the team learned,
 * and where a nav item sits is the fastest thing about it — you reach for
 * "Personal" at the bottom without reading, so the day-to-day screens come
 * first. Economie and Rapoarte are the exception the clinic asked for: the two
 * analysis screens sit at the end, after Laborator, rather than in the
 * reference system's place above Personal.
 *
 * One entry has no place in it, because it had no top-level place there: the
 * journal was Setări → Personalizare → Jurnal de activitate, and is a screen
 * of its own here, put beside the settings it used to live in.
 *
 * The waiting list is not in the rail either, and that is the original's
 * arrangement: it was never a section, it was a thing you opened when you had
 * a gap to fill. It opens from the command centre, where the rest of the
 * do-something buttons are.
 *
 * Labels are spelt as the old system spelt them: the team's muscle memory.
 */
/** Whether the user holds the entry's key, or any one of its keys. */
export function navAllowed(item: NavItem, can: (permission: string) => boolean): boolean {
  return !item.permission || [item.permission].flat().some((k) => can(k));
}

// `label` is a translation key in the `common` namespace, resolved at render
// time (this is a module-level constant, so `t` is not in scope here).
/**
 * Secțiunile barei. Exportate pentru „Meniul meu" (Setări → Aspectul meu),
 * care trebuie să ofere exact ce ar vedea omul — aceleași intrări, aceleași
 * permisiuni, aceleași module — nu o a doua listă care se desincronizează.
 */
export const NAV: NavItem[] = [
  { to: '/', label: 'navPanou', end: true },
  { to: '/calendar', label: 'navCalendar', permission: 'programari.vede' },
  // Cererile pacienților noi (supliment): lângă calendar, fiindcă acolo se
  // termină o cerere bună. Insigna e numărul celor la care nu s-a răspuns.
  {
    to: '/inquiries',
    label: 'navCereri',
    permission: 'cereri.vede',
    feature: 'patientInquiries',
    badge: 'inquiries',
  },
  { to: '/patients', label: 'navPacienti', permission: 'pacienti.vede_toti' },
  { to: '/payments', label: 'navAchitari', permission: 'incasari.vede', badge: 'payments' },
  {
    to: '/inventory',
    label: 'navStocuri',
    permission: 'stoc.vede',
    feature: 'inventory',
    badge: 'stock',
  },
  { to: '/imaging', label: 'navImagistica', permission: 'imagistica.vede', feature: 'imaging' },
  {
    to: '/chat',
    label: 'navChat',
    permission: 'chat.foloseste',
    feature: 'teamChat',
    badge: 'chat',
  },
  {
    to: '/hr',
    label: 'navResurseUmane',
    // Oricare din cheile de personal: proprietarul vede dosarele, aprobă
    // concediile și citește salariile, dar nu cere concediu el însuși, iar
    // legătura îi era ascunsă. Aceeași listă ca ruta, în `routes.tsx`.
    permission: [
      'concedii.cere',
      'concedii.vede',
      'personal.vede',
      'pontaj.vede',
      'salarii.vede',
      'recrutare.gestioneaza',
    ],
    feature: 'hr',
    badge: 'hr',
  },
  {
    to: '/lab',
    label: 'navLaboratorDentar',
    permission: 'laborator.vede',
    feature: 'dentalLab',
    badge: 'lab',
  },
  // Economie și Rapoarte, mutate sub Laborator dentar la cererea clinicii —
  // ecranele de analiză stau la capăt, după cele de zi cu zi.
  {
    to: '/economy',
    label: 'navEconomie',
    permission: 'financiar.vede',
    feature: 'economy',
    clinicWide: true,
  },
  // Registrul contabil (contabilitate faza A): al întregii entități, deci
  // ascuns celor limitați la unele filiale, ca Economie.
  {
    to: '/accounting',
    label: 'navContabilitate',
    permission: 'contabilitate.vede',
    feature: 'accounting',
    clinicWide: true,
  },
  {
    to: '/reports',
    label: 'navRapoarte',
    permission: 'rapoarte.vede',
    feature: 'reports',
    clinicWide: true,
  },
  { to: '/journal', label: 'navJurnal', permission: 'jurnal.vede' },
  // Fără cheie: fiecare om are în Setări cel puțin contul lui — parola,
  // programul, aspectul. Ce e al clinicii cere `setari.vede` pe fiecare filă
  // (hotărârea fondatorului din 18.09).
  { to: '/settings', label: 'navSetari' },
];

/**
 * Ghidul de utilizare stă pe site-ul platformei, nu în aplicație: îl citește și
 * cine n-are cont (un medic nou, înainte de prima zi; un pacient), și se
 * schimbă fără o versiune nouă a aplicației. Se deschide în limba aleasă aici;
 * ebraica, pe care site-ul n-o are, cade pe engleză.
 */
const GUIDE_URL: Record<string, string> = {
  ro: 'https://dentflow.md/ghid',
  ru: 'https://dentflow.md/ru/ghid',
  en: 'https://dentflow.md/en/ghid',
};
const guideUrl = (lang: string) => GUIDE_URL[lang] ?? GUIDE_URL.en;

export function AppLayout() {
  const { t, i18n } = useTranslation('common');
  const { tenant, can, hasFeature, user, demoView, setDemoView } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [panelOpen, setPanelOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  /** Lista de încasat se deschide sub pastilă, nu într-o altă pagină. */
  const [dueOpen, setDueOpen] = useState(false);
  /** Pastila „De încasat": panoul se desenează sub ea, într-un portal. */
  const [dueAnchor, setDueAnchor] = useState<HTMLButtonElement | null>(null);
  /*
   * Aspectul ales de om pentru el (Setări → Aspectul meu): tema, forma
   * ecranului și stilul calendarului. Stă pe contul lui, nu pe calculator, deci
   * îl urmează la orice scaun; ce n-a ales urmează clinica — iar tema, ce s-a
   * ales ultima dată pe acest calculator.
   */
  const queryClient = useQueryClient();
  const mine = useQuery({
    queryKey: ['auth', 'look'],
    queryFn: authApi.lookPrefs,
    enabled: user?.kind === 'staff',
    staleTime: 5 * 60_000,
  });
  const [deviceTheme, setDeviceTheme] = useState<'light' | 'dark'>(() =>
    document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light',
  );
  const theme = mine.data?.theme ?? deviceTheme;
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('df.theme', theme);
    } catch {
      /* private mode: the choice just does not persist */
    }
  }, [theme]);
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setDeviceTheme(next);
    // Butonul din bară e aceeași alegere ca „Tema" din Aspectul meu: se vede
    // pe loc și se scrie pe cont.
    if (user?.kind === 'staff') {
      queryClient.setQueryData<LookPrefs>(['auth', 'look'], (old) => ({ ...old, theme: next }));
      void authApi
        .setLookPrefs({ theme: next })
        .then((saved) => queryClient.setQueryData(['auth', 'look'], saved))
        .catch(() => undefined);
    }
  };

  // ── Live numbers for the rail badges, the De încasat pill and the
  //    command centre. One dashboard call plus the reception queue,
  //    refreshed every minute — the shell stays a glance, not a report. ──
  const dashboard = useQuery({
    queryKey: ['shell', 'dashboard'],
    queryFn: reportsApi.dashboard,
    refetchInterval: 60_000,
  });

  /*
   * The clinic's chosen shape. `index.html` stamps it before first paint from
   * what this machine already knows; this is where a machine that has never
   * seen it — or one where somebody has since changed it — catches up. Read
   * once per session, on the shell that every signed-in screen renders inside.
   *
   * From `GET /settings/look`, which any signed-in member may read, and not
   * from the full settings, which need `setari.vede` — held by the
   * administrator, the owner and HR alone. Reading it there meant the theme,
   * the calendar style, the chart drawing, the logo and the screen lock
   * reached three roles out of eight: never reception, never a doctor, never
   * an assistant. The lock is the plainest of them — the card says „the
   * computer at the front desk", and the front desk was the one session that
   * never received the number.
   *
   * The same key the status names use, so the shell and `StatusMetaProvider`
   * share one request.
   */
  const look = useQuery({
    queryKey: ['settings', 'look'],
    queryFn: settingsApi.look,
    staleTime: 5 * 60_000,
  });
  // Sigla e o cale în depozitul clinicii; linkul se semnează la citire și
  // ține o oră, deci se cere o dată pe sesiune și se ține în cache.
  const logoPath = look.data?.logo ?? null;
  const logo = useQuery({
    queryKey: ['files', 'url', logoPath],
    queryFn: () => filesApi.url(logoPath!),
    enabled: Boolean(logoPath),
    staleTime: 30 * 60_000,
  });
  const logoUrl = logo.data?.url ?? null;

  const myAppearance = mine.data?.appearance;
  const appearance = myAppearance
    ? myAppearance === 'standard'
      ? ''
      : myAppearance
    : (look.data?.appearance ?? '');
  const myCalStyle = mine.data?.calStyle;
  const calStyle = myCalStyle
    ? myCalStyle === 'compact'
      ? ''
      : myCalStyle
    : (look.data?.calStyle ?? '');
  const chartStyle = look.data?.chartStyle ?? '';
  // Ridicarea programării de sub cursor. Oprită implicit: e o mișcare pe
  // ecran, iar o clinică o pornește dacă i-o vrea.
  const calLift = look.data?.calLift === true;
  // Pulsul de atenție de pe cardurile calendarului. Pornit implicit — doar un
  // `false` explicit îl oprește, ca o clinică ce n-a deschis niciodată setarea
  // să-l păstreze.
  const calPulse = look.data?.calPulse !== false;
  // Forma pulsului: câte pulsuri, viteza fiecăruia, cât de des. Implicit 3/normal/10s.
  const calPulseCount = look.data?.calPulseCount ?? 3;
  const calPulseSpeed = look.data?.calPulseSpeed ?? 'normal';
  const calPulseCycle = look.data?.calPulseCycle ?? 10;
  // Evidențierea cardului abia creat — pornită implicit, ca pulsul.
  const calNewHighlight = look.data?.calNewHighlight !== false;
  // Mod calm: mai puțină mișcare în toată aplicația. Oprit până când o pornește clinica.
  const calmMode = look.data?.calmMode === true;
  // Setul de iconițe al barei, ales din Personalizare. Gol = „clasic".
  // Nu se aplică pe DOM ca temele: iconițele sunt componente, deci se citesc
  // aici și bara se redesenează când `look` se împrospătează.
  const iconSet = look.data?.iconSet ?? '';
  // Stilul barei (densitatea): „chips" (implicit), „active" sau „compact".
  // Ajunge pe rail ca atribut și restul e din CSS. Vezi `nav-icons.tsx`.
  const menuStyle = menuStyleKey(look.data?.menuStyle);
  // Minutes of inactivity before the screen is covered. 20 as in the original;
  // 0 switches it off for a clinic that would rather not have it.
  const lockAfterMin = (() => {
    const n = Number(look.data?.lockAfterMin);
    return Number.isFinite(n) && n >= 0 ? n : 20;
  })();
  useEffect(() => {
    if (look.data) {
      applyAppearance(appearance);
      applyCalStyle(calStyle);
      applyChartStyle(chartStyle);
      applyCalLift(calLift);
      applyCalPulse(calPulse);
      applyCalPulseBurst(calPulseCount, calPulseSpeed);
      applyCalPulseCycle(calPulseCycle);
      applyCalNewHighlight(calNewHighlight);
      applyCalmMode(calmMode);
    }
  }, [
    appearance,
    calStyle,
    chartStyle,
    calLift,
    calPulse,
    calPulseCount,
    calPulseSpeed,
    calPulseCycle,
    calNewHighlight,
    calmMode,
    look.data,
  ]);
  const canMoney = can('incasari.vede');
  const queue = useQuery({
    // Cheia stă sub „payments" intenționat: orice ecran care schimbă statutul
    // unei proceduri invalidează deja `['payments']`, deci pastila și lista
    // de sub ea se împrospătează în clipa în care medicul pune „Finalizat" —
    // nu la următorul minut de poll.
    // Doar ziua de azi (hotărârea din 18.09): pastila e despre pacienții care
    // pleacă acum de pe scaun; datornicii zilelor trecute se lucrează în Achitări.
    queryKey: ['payments', 'due-queue', 'today'],
    queryFn: () => paymentsApi.unpaidProcedures(undefined, { finishedToday: true }),
    enabled: canMoney,
    refetchInterval: 60_000,
  });
  const canCal = can('programari.vede');
  /*
   * Ziua pentru care se sună (CAL-9): prima în care clinica lucrează, de la
   * server. „Mâine” calendaristic număra vineri seara sâmbăta, zi închisă, și
   * uita lunea, pentru care chiar trebuia sunat.
   */
  const nextOpen = useQuery({
    queryKey: ['appointments', 'next-open-day'],
    queryFn: appointmentsApi.nextOpenDay,
    enabled: canCal,
    staleTime: 5 * 60_000,
  });
  const calendarTomorrow = addDays(toIsoDate(new Date()), 1);
  const tomorrow = nextOpen.data?.date ?? calendarTomorrow;
  const confirmDay =
    tomorrow === calendarTomorrow
      ? t('maine')
      : t('pentruZi', {
          day: new Date(`${tomorrow}T00:00:00`).toLocaleDateString(i18n.language, {
            weekday: 'long',
          }),
        });
  const tomorrowAppts = useQuery({
    queryKey: ['shell', 'tomorrow', tomorrow],
    queryFn: () => appointmentsApi.list({ from: tomorrow, to: tomorrow }),
    enabled: canCal,
    refetchInterval: 5 * 60_000,
  });

  const queuePatients = useMemo(() => {
    const set = new Set((queue.data ?? []).map((r) => r.patientId));
    return set.size;
  }, [queue.data]);
  const queueSum = useMemo(
    () => (queue.data ?? []).reduce((s, r) => s + Number(r.due), 0),
    [queue.data],
  );
  const tomorrowUnconfirmed = useMemo(
    () => (tomorrowAppts.data ?? []).filter((a) => a.status === 'scheduled').length,
    [tomorrowAppts.data],
  );

  /*
   * Necitite pe „Chat", ca în original.
   *
   * Cerere proprie, nu din tabloul de bord: dreptul de a vedea un canal se
   * decide pe fiecare canal, iar un total scris ca o singură interogare ar
   * număra și mesajele dintr-un canal închis. Se împrospătează la un minut,
   * ca restul insignelor.
   */
  const chatUnread = useQuery({
    queryKey: ['chat', 'unread'],
    queryFn: chatApi.unread,
    // Not asked for at a clinic without chat: the API answers 404 there.
    enabled: can('chat.foloseste') && hasFeature('teamChat'),
    refetchInterval: 60_000,
  });

  /*
   * Cererile la care nu a răspuns încă nimeni. Cerere proprie, la un minut,
   * ca restul insignelor; deloc la o clinică fără supliment (404 acolo).
   */
  const inquiriesWaiting = useQuery({
    queryKey: ['inquiries', 'summary'],
    queryFn: inquiriesApi.summary,
    enabled: can('cereri.vede') && hasFeature('patientInquiries'),
    refetchInterval: 60_000,
  });

  const badges: Record<string, number> = {
    payments: queuePatients,
    stock: dashboard.data?.alerts.lowStock ?? 0,
    waitlist: dashboard.data?.alerts.pendingRequests ?? 0,
    chat: chatUnread.data?.total ?? 0,
    // Ce așteaptă pe „Personal": o cerere de concediu nedecisă și un act care
    // stă să expire sunt amândouă lucruri care se rezolvă azi și se uită luni.
    hr:
      (dashboard.data?.alerts.pendingLeave ?? 0) + (dashboard.data?.alerts.expiringStaffDocs ?? 0),
    lab: dashboard.data?.alerts.overdueLab ?? 0,
    inquiries: inquiriesWaiting.data?.waiting ?? 0,
  };

  /*
   * Scurtăturile de la tastatură ale originalului.
   *
   * O literă singură, fără modificator, și numai când nu scrii undeva: `d`
   * dashboard, `n` calendar (de la „nou"), `p` pacienți, `i` stocuri (de la
   * „inventar"), `c` chat. Recepția le folosește toată ziua, iar mâna nu
   * pleacă de pe tastatură între un pacient și următorul.
   *
   * Spre deosebire de original, respectă permisiunile: acolo `i` deschidea
   * Stocuri chiar pentru cine n-avea voie să le vadă, iar pagina se randa
   * fiindcă protecția era doar pe meniu.
   */
  const HOTKEYS: Record<string, string> = {
    d: '/',
    n: '/calendar',
    p: '/patients',
    i: '/inventory',
    c: '/chat',
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      // Nu în timp ce scrii: un câmp, un textarea sau orice zonă editabilă.
      if (target && target !== document.body) return;
      const to = HOTKEYS[e.key.toLowerCase()];
      if (!to) return;
      const item = NAV.find((n) => n.to === to);
      if (item && !navAllowed(item, can)) return;
      if (item?.feature && !hasFeature(item.feature)) return;
      if (item?.clinicWide && branchLimitOf(user)) return;
      e.preventDefault();
      navigate(to, to === '/' ? { state: OPEN_PANOU } : undefined);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can, hasFeature, navigate, user]);

  const limited = branchLimitOf(user) !== null;
  /*
   * Și ce și-a scos omul ăsta din meniul lui (Setări → Aspectul meu).
   *
   * Strict o vedere: ruta rămâne deschisă, Centrul de Comandă găsește
   * secțiunea, iar permisiunile nu se ating. De asta filtrul e aici, la
   * desenarea barei, și nicăieri altundeva — un cabinet de doi oameni nu
   * trece pe lângă Laborator și Resurse umane de douăzeci de ori pe zi, dar
   * un link spre ele tot funcționează.
   */
  const menuHidden = new Set(mine.data?.menuHidden ?? []);
  const visible = NAV.filter(
    (item) =>
      navAllowed(item, can) &&
      (!item.feature || hasFeature(item.feature)) &&
      !(item.clinicWide && limited) &&
      !menuHidden.has(item.to),
  );
  /* Setările stau jos, despărțite de restul.
     Nu sunt un loc în care se lucrează — sunt locul în care se schimbă cum
     lucrează celelalte. Toate barele care le poartă le țin la capăt, iar de
     când contul a plecat în Centrul de Comandă spațiul de jos era gol. */
  const items = visible.filter((i) => i.to !== '/settings');
  const settingsItem = visible.find((i) => i.to === '/settings');

  const renderLink = (item: NavItem) => {
    const count = item.badge ? badges[item.badge] : 0;
    const label = t(item.label);
    return (
      <NavLink
        key={item.to}
        to={item.to}
        // „Panou" deschide Panoul și când clinica pornește în Calendar.
        state={item.to === '/' ? OPEN_PANOU : undefined}
        end={item.end}
        // Numele apare ca tooltip — necesar în „doar iconițe", unde eticheta e
        // ascunsă, dar nedăunător în rest.
        title={label}
        className={({ isActive }) =>
          ['shell__link', isActive ? 'shell__link--active' : ''].filter(Boolean).join(' ')
        }
      >
        <span className="shell__linkicon">
          {navIcon(iconSet, item.to)}
          {/* „9+" peste nouă, ca în original: pastila stă peste o iconiță de
              18px și trei cifre o fac cât ea. */}
          {count > 0 ? (
            <span className="shell__badge" title={`${label}: ${count}`}>
              {count > 9 ? '9+' : count}
            </span>
          ) : null}
        </span>
        <span className={`shell__linklabel${label.includes(' ') ? ' shell__linklabel--wrap' : ''}`}>
          {label}
        </span>
      </NavLink>
    );
  };

  /*
   * Titlul barei de sus. Regula e în `pageTitle.ts`, cu testele ei — rezerva a
   * fost șirul gol de două ori, iar un `<h1>` gol nu se vede de pe ecran.
   */
  const clinicName = tenant?.branding.displayName ?? tenant?.name ?? '';
  const pageTitle = pageTitleFor(location.pathname, NAV, clinicName, (key) => t(key));

  return (
    <StatusMetaProvider>
      <CalendarPrefsProvider>
        <IdleLock minutes={lockAfterMin} />
        <OfflineBanner />
        <div className="shell">
          <a href="#main" className="sr-only">
            {t('sariLaContinut')}
          </a>
          <InstallPrompt />

          {/* ── The rail: icons over tiny labels, badges where numbers wait. ── */}
          <aside className="shell__rail" data-menu-style={menuStyle}>
            {/* Sigla clinicii nu mai stă aici, ci lângă numele clinicii din
              antet (shell__pagehead) — un singur loc, unde o vede oricine, și
              unde încape și sigla încărcată, nu doar inițiala. */}
            <nav className="shell__nav" aria-label={t('navigarePrincipala')}>
              {items.map(renderLink)}
              <div className="shell__navfoot">
                <a
                  className="shell__link"
                  href={guideUrl(i18n.language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t('navGhid')}
                >
                  <span className="shell__linkicon">
                    <BookOpen size={18} aria-hidden />
                  </span>
                  <span className="shell__linklabel">{t('navGhid')}</span>
                </a>
                {can('asistent.foloseste') ? (
                  <button
                    type="button"
                    className={['shell__assistant', assistantOpen ? 'is-on' : ''].join(' ')}
                    onClick={() => setAssistantOpen((o) => !o)}
                    aria-pressed={assistantOpen}
                    aria-label={t('asistentVirtual')}
                    title={t('asistentVirtual')}
                  >
                    <span className="shell__assistanticon" aria-hidden>
                      <Sparkles size={18} />
                    </span>
                    <span className="shell__linklabel">{t('asistentNav')}</span>
                  </button>
                ) : null}
                {settingsItem ? renderLink(settingsItem) : null}
              </div>
            </nav>

            {/* Contul stă în Centrul de Comandă, nu aici.
              Motivul scris când era în bară — „contul e al aplicației, nu al
              paginii" — duce acolo: panoul e nivelul aplicației și ține deja
              tema, iar bara asta e pentru navigare. */}
          </aside>

          <div className="shell__body">
            <header className="shell__topbar">
              <div className="shell__pagehead">
                {/* Sigla clinicii lângă nume: sigla încărcată, ori inițiala pe
                  culoarea clinicii când nu e niciuna. */}
                <div className="shell__headmark" aria-hidden>
                  {logoUrl ? (
                    <img src={logoUrl} alt="" className="shell__headlogo" />
                  ) : (
                    (tenant?.branding.displayName ?? 'D').slice(0, 1).toUpperCase()
                  )}
                </div>
                <div className="shell__pagehead-text">
                  {/* Nu e `<h1>` (D5): titlul principal e al paginii, iar bara îl repetă
                    ca să se vadă unde ești. Două `<h1>` citeau pentru un cititor
                    de ecran ca două pagini. */}
                  <p className="shell__pagetitle">{t(pageTitle)}</p>
                  {/* Pe o rută fără nume, titlul *este* numele clinicii — scris de
                    două ori, una sub alta, ar citi ca o greșeală. */}
                  {pageTitle === clinicName ? null : (
                    <span className="shell__pagesub">{clinicName}</span>
                  )}
                </div>
              </div>

              <div className="shell__topright">
                {/* Ce i se arată vizitatorului demo, și drumul înapoi.
                    Eticheta e obligatorie, nu decorativă: fără ea, cine a ales
                    „Cabinet solo" n-are cum să afle de ce lipsește Laboratorul
                    și citește demo-ul ca pe o aplicație stricată. Pe o clinică
                    adevărată nu se desenează deloc. */}
                {tenant?.demo ? (
                  <span className="shell__demoview">
                    <MoreMenu
                      align="end"
                      label={`${t('demoVedere')}: ${demoView ? t(demoView.labelKey) : t('demoTot')}`}
                      items={[
                        ...DEMO_VIEWS.map((v) => ({
                          label: t(v.labelKey),
                          hint: t(v.hintKey),
                          onSelect: () => setDemoView(v.key),
                        })),
                        {
                          label: t('demoTot'),
                          hint: t('demoTotHint'),
                          onSelect: () => setDemoView(null),
                        },
                      ]}
                    />
                  </span>
                ) : null}
                {canMoney && queuePatients > 0 ? (
                  <div className="shell__duewrap">
                    <button
                      type="button"
                      ref={setDueAnchor}
                      className="shell__due"
                      aria-expanded={dueOpen}
                      title={`${t('pacienti', { count: queuePatients })} · ${formatMoney(Math.round(queueSum), tenant?.currency ?? 'MDL')} de încasat azi`}
                      onClick={() => setDueOpen((o) => !o)}
                    >
                      <span aria-hidden>💳</span>
                      <span className="shell__duelabel">{t('deIncasat')}</span>
                      <span className="shell__duecount">{queuePatients}</span>
                      <strong className="tabular">{formatNumber(Math.round(queueSum))}</strong>
                    </button>
                    {dueOpen ? (
                      <DuePanel
                        rows={queue.data ?? []}
                        currency={tenant?.currency ?? 'MDL'}
                        anchor={dueAnchor}
                        onClose={() => setDueOpen(false)}
                      />
                    ) : null}
                  </div>
                ) : null}
                <button
                  type="button"
                  className={['shell__cc', panelOpen ? 'shell__cc--open' : ''].join(' ')}
                  onClick={() => setPanelOpen((p) => !p)}
                >
                  <Activity size={12} aria-hidden />
                  <span className="shell__cclabel">
                    {panelOpen ? t('inchide') : t('centruComanda')}
                  </span>
                </button>
                <NotificationBell />
              </div>
            </header>

            <div className="shell__content">
              <main
                className={`shell__main ${/^\/patients(\/[^/]+)?$/.test(location.pathname) ? 'shell__main--flush' : ''}`}
                id="main"
              >
                {/* Above the page, inside the scroll area: a suggestion should
                  not permanently occupy a fixed strip of a dense screen. */}
                <TwoFactorNudge />
                {/* „Aici faci asta", o dată per ecran și per om. Deasupra
                  paginii, în aria care se derulează: un sfat n-are voie să
                  ocupe permanent o fâșie fixă dintr-un ecran dens. */}
                <ScreenHint />
                <Outlet />
              </main>

              {panelOpen ? (
                <CommandCentre
                  dashboard={dashboard.data}
                  queuePatients={queuePatients}
                  queueSum={queueSum}
                  tomorrowUnconfirmed={tomorrowUnconfirmed}
                  confirmDay={confirmDay}
                  theme={theme}
                  onToggleTheme={toggleTheme}
                  onClose={() => setPanelOpen(false)}
                />
              ) : null}
              {assistantOpen ? <AssistantPanel onClose={() => setAssistantOpen(false)} /> : null}
            </div>
          </div>
        </div>
      </CalendarPrefsProvider>
    </StatusMetaProvider>
  );
}
